(function($, moment) {
	"use strict";

	/**
	 * Cette classe représente un critère de filtre dans un onglet en contenant plusieurs.
	 * 
	 * @member {string} name - le nom interne du critère
	 * @member {string} title - le libellé du critère visible par l'utilisateur
	 * @member {function(filter)} check - une fonction indiquant si le filtre fourni est actif
	 * @member {function(filter)} clear - une fonction désactivant le filtre pour ce critère
	 * @member {function(filter, cell1, cell2, lang, editable)} load - une fonction chargeant l'IHM de saisie de ce filtre pour ce critère
	 * @member {function(filter, cell1, cell2, lang)} save - une fonction sauvegardant les modifications apportées à ce filtre pour ce critère
	 */
	function ScopeCriteria(name, title, check, clear, load, save) {
		this.name = name;
		this.title = title;
		this.check = check;
		this.clear = clear;
		this.load = load;
		this.save = save;
	}

	/** Un critère de filtre booléen (NotFiltered, IsNull, IsTrue, IsFalse, IsNotNull, IsNotTrue, IsNotFalse) */
	function createBooleanCriteria(name, title) {
		function check(filter) {
			return (filter.operator || 'NotFiltered') !== 'NotFiltered';
		}
		function clear(filter) {
			delete filter.operator;
		}
		function load(filter, cell1, cell2, lang, editable) {
			var options = ['NotFiltered', 'IsNull', 'IsTrue', 'IsFalse', 'IsNotNull', 'IsNotTrue', 'IsNotFalse'].map(function(operator) {
				return $('<option />').attr('value', operator).text(lang.operators[operator]).get(0);
			});
			$('<select class="form-control" style="padding-right: 16px;" />')
				.prop('disabled', !editable)
				.append(options)
				.val(filter.operator || 'NotFiltered')
				.appendTo(cell1);
		}
		function save(filter, cell1, cell2, lang) {
			filter.operator = cell1.children('select').val();
		}
		return new ScopeCriteria(name, title, check, clear, load, save);
	}

	/** Un critère de filtre textuel (NotFiltered, Equal ..., Different ..., IsNull, IsNotNull, Like ..., NotLike ...) */
	function createTextCriteria(name, title) {
		function check(filter) {
			if (!filter.operator || (filter.operator === 'NotFiltered'))
				return false;
			if (filter.operator === 'IsNull' || filter.operator === 'IsNotNull')
				return true;
			return !!filter.pattern;
		}
		function clear(filter) {
			delete filter.operator;
			delete filter.pattern;
		}
		function load(filter, cell1, cell2, lang, editable) {
			var options = ['NotFiltered', 'Equal', 'Different', 'IsNull', 'IsNotNull', 'Like', 'NotLike'].map(function(operator) {
				return $('<option />').attr('value', operator).text(lang.operators[operator]).get(0);
			});
			var select = $('<select class="form-control" style="padding-right: 16px;" />')
				.prop('disabled', !editable)
				.append(options)
				.val(filter.operator || 'NotFiltered')
				.appendTo(cell1);
			var input = $('<input class="form-control" type="text" />')
				.prop('disabled', !editable)
				.attr('placeholder', lang['PatternPlaceholder'])
				.val(filter.pattern || '')
				.appendTo(cell2);
			select.on('change', function() {
				var op = select.val();
				var usePattern = op === 'Equal' || op === 'Different' || op === 'Like' || op === 'NotLike';
				input.toggle(usePattern);
				if (! usePattern)
					input.val('');
			}).change();
		}
		function save(filter, cell1, cell2, lang) {
			filter.operator = cell1.children('select').val();
			filter.pattern = cell2.children('input').val();
		}
		return new ScopeCriteria(name, title, check, clear, load, save);
	}

	/** Un critère de filtre numérique */
	function createNumberCriteria(name, title) {
		function check(filter) {
			return (typeof filter.min === 'number') || (typeof filter.max === 'number');
		}
		function clear(filter) {
			delete filter.min;
			delete filter.max;
		}
		function load(filter, cell1, cell2, lang, editable) {
			var i1 = $('<input type="text" class="form-control" />')
				.prop('disabled', !editable)
				.attr('placeholder', lang['MinPlaceholder']).appendTo(cell1);
			var i2 = $('<input type="text" class="form-control" />')
				.prop('disabled', !editable)
				.attr('placeholder', lang['MaxPlaceholder']).appendTo(cell2);
			if (typeof filter.min === 'number')
				i1.val(filter.min.toString());
			if (typeof filter.max === 'number')
				i2.val(filter.max.toString());
		}
		function save(filter, cell1, cell2, lang) {
			var v1 = cell1.children('input').val() || '';
			var v2 = cell2.children('input').val() || '';
			try {
				filter.min = v1 ? Number.parseFloat(v1) : null;
				filter.max = v2 ? Number.parseFloat(v2) : null;
			} catch (ex) {
				//
			}
		}
		return new ScopeCriteria(name, title, check, clear, load, save);
	}

	/** Un critère de filtre date et/ou heure */
	function createDateTimeCriteria(name, title, pickDate, pickTime, displayFormat) {
		displayFormat = displayFormat || ((pickDate ? 'L' : '') + (pickDate && pickTime ? ' ' : '') + (pickTime ? 'LT' : ''));

		function buildFilterInput(cell, placeholder, locale, value, editable) {
			// Formattage de value (un "long") en texte pour l'utilisateur
			var text = value ? moment(value).format(displayFormat) : '';
			// Ajout de l'input avec valeur dans le parent
			var input = $('<input class="form-control" type="text" />').attr('placeholder', placeholder).val(text).appendTo(cell);
			// Désactivation de l'input si l'univers est en lecture-seule
			input.prop('disabled', !editable);
			// Si le composant "Bootstrap Datetimepicker" est dispo, on l'utilise car plus joli qu'un <input type="text" />
			if ($.fn.datetimepicker) {
				input.wrap('<div style="position: relative;" />').datetimepicker({
					locale: locale,
					format: displayFormat,
					useCurrent: false,
					calendarWeeks: true
				});
			}
		}
		function applyFilterInput(cell) {
			// Récupération de la valeur
			var text = cell.find('input').val();
			if (text) {
				try {
					// Parser la date
					var value = moment(text, displayFormat);
					// Ajuster en "date" ou "heure" si l'un des deux doit être tronqué
					if (!pickDate)
						value = value.year(1970).month(0).date(1);
					if (!pickTime)
						value = value.startOf('day');
					// Renvoi de la valeur sous la forme d'un long
					return value.toDate().getTime();
				} catch (ex) {
					//
				}
			}
			return null;
		}
		function check(filter) {
			return (typeof filter.min === 'number') || (typeof filter.max === 'number');
		}
		function clear(filter) {
			delete filter.min;
			delete filter.max;
		}
		function load(filter, cell1, cell2, lang, editable) {
			buildFilterInput(cell1, lang['MinPlaceholder'], lang['Locale'], filter.min, editable);
			buildFilterInput(cell2, lang['MaxPlaceholder'], lang['Locale'], filter.max, editable);
		}
		function save(filter, cell1, cell2, lang) {
			filter.min = applyFilterInput(cell1);
			filter.max = applyFilterInput(cell2);
		}
		return new ScopeCriteria(name, title, check, clear, load, save);
	}

	/** Un critère de filtre sur liste */
	function createListCriteria(name, title, options) {
		function check(filter) {
			return !!filter.selection && filter.selection.length > 0;
		}
		function clear(filter) {
			delete filter.selection;
			delete filter.include;
		}
		function load(filter, cell1, cell2, lang, editable) {
			$('<select class="form-control" style="padding-right: 16px;" />')
				.append($('<option value="Equal" />').text(lang.operators['Equal']))
				.append($('<option value="Different" />').text(lang.operators['Different']))
				.val((filter.include === false) ? 'Different' : 'Equal')
				.prop('disabled', !editable)
				.appendTo(cell1);
			$('<select class="form-control" multiple />')
				.append(options.map(function(option) {
					return $('<option />').attr('value', option.id.toString()).text(option.caption)
						.prop('selected', filter.selection && filter.selection.includes(option.id))
						.get(0);
				}))
				.prop('disabled', !editable)
				.appendTo(cell2);
			// Si le composant "Bootstrap Multiselect" est dispo, on l'utilise car plus joli qu'un <select multiple />
			if (!! $.fn.multiselect) {
				cell2.children().multiselect({
					buttonWidth: '100%',
					dropRight: true,
					maxHeight: 200,
					enableFiltering: true,
					enableCaseInsensitiveFiltering : true,
					nonSelectedText: '...',
					numberDisplayed: Number.MAX_VALUE
				});
			}
		}
		function save(filter, cell1, cell2, lang) {
			filter.include = cell1.children('select').val() === 'Equal';
			filter.selection = (cell2.find('select[multiple]').val() || []).map(function(s) { return Number.parseInt(s); });
		}
		return new ScopeCriteria(name, title, check, clear, load, save);
	}

	/**
	 * Cette classe représente un onglet dans le popin univers
	 * 
	 * @member {string} title - le titre de l'onglet
	 * @member {function(scope)} check - une fonction indiquant si l'onglet est actif pour un univers donné
	 * @member {function(scope)} clear - une fonction désactivant cet onglet pour un univers donné
	 * @member {function(scope, div, lang, editable)} load - une fonction chargeant l'IHM de saisie de cet univers pour cet onglet
	 * @member {function(scope, div, lang)} save - une fonction sauvegardant les modifications apportées à cet univers par cet onglet
	 * @member {function(div)} status - une fonction indiquant si l'onglet doit apparaitre comme actif (avec une * à côté du nom)
	 */
	function ScopeTab(title, check, clear, load, save, status) {
		this.title = title;
		this.check = check;
		this.clear = clear;
		this.load = load;
		this.save = save;
		this.status = status;
	}

	/** Un onglet de l'univers utilisé pour une liste importante, comme "Enseignes", "Marques", ... */
	function createListTab(name, title, options, selectableProperty) {
		function check(scope) {
			var c = scope[name];
			return (c && c.selection) ? c.selection.length > 0 : false;
		}
		function clear(scope) {
			var c = scope[name];
			if (c) {
				delete c.selection;
				delete c.include;
			}
		}
		function load(scope, div, lang, editable) {
			// Récupération du filtre en cours dans l'univers
			var c = scope[name];
			var include = !c || c.include !== false;
			var selection = (c && c.selection) || [];

			// Création du div pour les boutons-radios "Égal à" / "Différent de"
			var header = $(''
					+ '<div style="text-align: center; flex: none; ">'
					+ '  <label><input type="radio" value="true" /> ' + lang.operators['Equal'] + '</label>'
					+ '  <span style="display: inline-block; width: 20px;" />'
					+ '  <label><input type="radio" value="false" /> ' + lang.operators['Different'] + '</label>'
					+ '</div>');
			header.find('input').attr('name', 'include.' + name).prop('disabled', !editable)
				.first().prop('checked', include).end()
				.last().prop('checked', !include).end();

			// Création du div pour la liste de sélection
			var list = $('<div class="list-group" style="flex: auto; overflow-y: auto; margin-bottom: 0; " />');
			list.append($.map(options, function(o) {
				var selectable = o[selectableProperty] !== false; // null or true
				var selected = selection.includes(o.id);
				if (!selected && !selectable)
					return; // remove this option
				return $('<div class="list-group-item" />').toggleClass('bbm-scope-selected', selected).data('option', o).text(o.caption).get(0);
			}));

			// Changement de sélection
			if (editable) {
				list.on('click', '.list-group-item', function(event) {
					$(event.target).closest('.list-group-item').toggleClass('bbm-scope-selected');
				});
			}

			// OK
			div.append(header).append(list);
		}
		function save(scope, div, lang) {
			// Extraire le formulaire
			var include = div.children(':first-child').find('input:checked').val() === 'true';
			var selection = div.find('.bbm-scope-selected').map(function(i, n) { return $(n).data('option').id; }).get();

			// Aucune sélection signifie que le filtre est inactif
			if (selection.length === 0) {
				clear(scope);
				return;
			}

			// Appliquer le filtre, en créant si besoin l'objet dans l'univers
			var c = scope[name];
			if (!c)
				scope[name] = c = {};
			c.include = include;
			c.selection = selection;
		}
		function status(div) {
			return div.find('.bbm-scope-selected').length > 0;
		}
		return new ScopeTab(title, check, clear, load, save, status);
	}

	/** Un onglet de l'univers utilisé pour une liste importante, comme "Enseignes", "Marques", "Centrales", "Secteurs", ... */
	function createListOrTreeTab(name, title, options, selectableProperty) {
		function check(scope) {
			var c = scope[name];
			return (c && c.selection) ? c.selection.length > 0 : false;
		}
		function clear(scope) {
			var c = scope[name];
			if (c) {
				delete c.selection;
				delete c.include;
			}
		}
		function load(scope, div, lang, editable) {
			// Récupération du filtre en cours dans l'univers
			var c = scope[name];
			var include = !c || c.include !== false;
			var selection = (c && c.selection) || [];

			// Préparation de la liste d'options pour "materialTree"
			var treeOptions = $.map(options, function toMaterialTree(o) {
				var selectable = o[selectableProperty] !== false; // null or true
				var selected = selection.includes(o.id);
				if (!selected && !selectable)
					return; // remove this option
				var children = o.children ? $.map(o.children, toMaterialTree) : undefined;
				return {
					id: o.id,
					text: o.caption,
					selectable: selected || editable,
					selected: selected,
					expanded: !!children && children.length > 0,
					children: children
				};
			});

			// Création du div pour les boutons-radios "Égal à" / "Différent de"
			var header = $(''
					+ '<div style="text-align: center; flex: none; ">'
					+ '  <label><input type="radio" value="true" /> ' + lang.operators['Equal'] + '</label>'
					+ '  <span style="display: inline-block; width: 20px;" />'
					+ '  <label><input type="radio" value="false" /> ' + lang.operators['Different'] + '</label>'
					+ '</div>');
			header.find('input').attr('name', 'include.' + name).prop('disabled', !editable)
				.first().prop('checked', include).end()
				.last().prop('checked', !include).end();

			// Création du div pour la liste (ou l'arbre) de sélection
			var tree = $('<div class="tree" style="flex: auto; overflow-y: auto; " />');
			tree.materialTree({
				items: treeOptions,
				customize: function($li, item) {
					$li.attr('data-value', item.id);
				}
			});

			// OK
			div.append(header).append(tree);
		}
		function save(scope, div, lang) {
			// Extraire le formulaire
			var include = div.children(':first-child').find('input:checked').val() === 'true';
			var selection = div.children('.tree').find('.bbm-tree-selected').map(function(i, n) {
				return parseInt(n.getAttribute('data-value'));
			}).get();

			// Aucune sélection signifie que le filtre est inactif
			if (selection.length === 0) {
				clear(scope);
				return;
			}

			// Appliquer le filtre, en créant si besoin l'objet dans l'univers
			var c = scope[name];
			if (!c)
				scope[name] = c = {};
			c.include = include;
			c.selection = selection;
		}
		function status(div) {
			return div.children('.tree').find('.bbm-tree-selected').length > 0;
		}
		return new ScopeTab(title, check, clear, load, save, status);
	}

	/** Un onglet de l'univers utilisé pour une grille, comme "Clients" ou "Articles" */
	function createGridTab(name, title, columns, loader) {
		function check(scope) {
			var c = scope[name];
			return (c && c.selection) ? c.selection.length > 0 : false;
		}
		function clear(scope) {
			var c = scope[name];
			if (c) {
				delete c.selection;
				delete c.include;
			}
		}
		function load(scope, div, lang, editable) {
			// Récupération du filtre en cours dans l'univers
			var c = scope[name];
			var include = !c || c.include !== false;
			var selection = [].concat(c && c.selection);

			// Création du div pour les boutons-radios "Égal à" / "Différent de"
			var header = $(''
					+ '<div style="text-align: center; flex: none; ">'
					+ '  <label><input type="radio" value="true" /> ' + lang.operators['Equal'] + '</label>'
					+ '  <span style="display: inline-block; width: 20px;" />'
					+ '  <label><input type="radio" value="false" /> ' + lang.operators['Different'] + '</label>'
					+ '</div>');
			header.find('input').attr('name', 'include.' + name).prop('disabled', !editable)
				.first().prop('checked', include).end()
				.last().prop('checked', !include).end();

			// Création du div (en position relative) pour contenir la grille (en position absolue)
			var grid = $(''
					+ '<div style="flex: auto; position: relative; ">'
					+ '  <div style="text-align: center; "><i class="material-icons" style="margin: 20px; ">refresh</i></div>'
					+ '</div>');

			// Récupération de ligne (de manière asynchrone la première fois, puis on réutilise la liste)
			loader().then(function(rows) {
				// Création de la grille
				grid.children().empty().css('text-align', '').grid({
					lang: lang['Locale'],
					rows : rows,
					columns : columns,
					bordered: false,
					pageSize: false, // pas de pagination
					selectable: false, // on gère nous-même la multi-sélection
					editable: false, // pas de saisie
				});
			});

			// Affichage de la sélection
			grid.on('grid.pagechanged', function() {
				grid.find('.bbm-grid-row').each(function() {
					var tr = $(this);
					var row = tr.data('row');
					var selected = selection.includes(row.id);
					tr.toggleClass('bbm-scope-selected', selected);
				});
			});

			// Modification de la sélection
			if (editable) {
				grid.on('click', '.bbm-grid-row', function(event) {
					var tr = $(event.target).closest('.bbm-grid-row');
					var row = tr.data('row');
					var selectedIndex = selection.indexOf(row.id);
					if (selectedIndex >= 0) {
						selection.splice(selectedIndex, 1);
					} else {
						selection.push(row.id);
					}
					tr.toggleClass('bbm-scope-selected', selectedIndex === -1);
				});
			}

			// OK
			div.data('selection', selection).append(header).append(grid);
		}
		function save(scope, div, lang) {
			// Extraire le formulaire
			var include = div.children(':first-child').find('input:checked').val() === 'true';
			var selection = div.find('.bbm-scope-selected').map(function(i, n) {
				return $(this).data('row').id;
			}).get();

			// Aucune sélection signifie que le filtre est inactif
			if (selection.length === 0) {
				clear(scope);
				return;
			}

			// Appliquer le filtre, en créant si besoin l'objet dans l'univers
			var c = scope[name];
			if (!c)
				scope[name] = c = {};
			c.include = include;
			c.selection = selection;
		}
		function status(div) {
			return div.find('.bbm-scope-selected').length > 0;
		}
		return new ScopeTab(title, check, clear, load, save, status);
	}

	/** Un onglet de l'univers utilisé pour plusieurs critères d'un coup, comme le reste des champs clients */
	function createMultiTab(title, criterias) {
		function check(scope) {
			return criterias.some(function(c) { return scope[c.name] && c.check(scope[c.name]); });
		}
		function clear(scope) {
			criterias.forEach(function(c) { if (scope[c.name]) c.clear(scope[c.name]); });
		}
		function load(scope, div, lang, editable) {
			// Créer le menu déroulant pour ajouter les filtres inactifs (et où l'on cache les filtres actifs)
			var header = $(''
					+ '<div style="flex: none;">'
					+ '  <div class="dropdown">'
					+ '    <button class="btn btn-link dropdown-toggle" type="button" data-toggle="dropdown">'
					+ '      ' + lang['AddButton'] + ' <span class="caret"></span>'
					+ '    </button>'
					+ '    <ul class="dropdown-menu"></ul>'
					+ '  </div>'
					+ '</div>');

			// Créer la table où l'on voit les filtres actifs (et on masque les filtres inactifs)
			var content = $(''
					+ '<div style="flex: auto; overflow-y: auto; ">'
					+ '  <table style="width: 100%; max-width: 100%; table-layout: fixed; ">'
					+ '    <thead>'
					+ '      <tr>'
					+ '        <th style="width: 24px; ">&nbsp;</th>'
					+ '        <th style="width: auto; ">' + lang['FieldColumn'] + '</th>'
					+ '        <th style="width: auto; ">' + lang['OperatorColumn'] + '</th>'
					+ '        <th style="width: auto; ">' + lang['ValueColumn'] + '</th>'
					+ '      </tr>'
					+ '    </thead>'
					+ '    <tbody>'
					+ '    </tbody>'
					+ '  </table>'
					+ '</div>');

			// Ajouter l'entrée de menu et la ligne pour chaque filtre
			var ul = header.find('ul');
			var tbody = content.find('tbody'); 
			criterias.forEach(function(criteria) {
				var filter = scope[criteria.name];
				var active = filter && criteria.check(filter);
				// Créer une entrée de menu
				$('<li><a href="#" /></li>')
					.toggle(!active).appendTo(ul)
					.children().text(criteria.title).end();
				// Créer une ligne dans la table
				var tr = $('<tr><td><a href="#" class="material-icons bbm-scope-delete">delete</a></td><td></td><td></td><td></td></tr>')
					.toggle(active).appendTo(tbody);
				tr.find('.bbm-scope-delete').toggle(!!editable);
				tr.children().eq(1).text(criteria.title);
				// Préparer la zone spécifique au type de filtre dans les 3ème et 4ème colonnes  
				criteria.load(filter || {}, tr.children().eq(2), tr.children().eq(3), lang, editable);
			});

			// Ajout et suppression manuels de critères via le menu
			header.toggle(editable);
			ul.on('click', 'a', function(event) {
				// Masquer l'entrée de menu
				var li = $(event.target).closest('li').hide();
				// Afficher la ligne dans la table
				tbody.children().eq(li.index()).show();
				// Eviter le # dans l'URL
				event.preventDefault();
			});
			content.on('click', '.bbm-scope-delete', function(event) {
				// Masquer la ligne dans la table
				var tr = $(event.target).closest('tr').hide();
				// Afficher l'entrée de menu
				ul.children().eq(tr.index()).show();
				// Eviter le # dans l'URL
				event.preventDefault();
			});

			// OK
			div.append(header).append(content);
		}
		function save(scope, div, lang) {
			div.find('tbody > tr').each(function(index) {
				var tr = $(this);
				var criteria = criterias[index];
				var filter = scope[criteria.name];
				if (this.style.display === 'none') {
					if (filter)
						criteria.clear(filter);
				} else {
					if (!filter)
						filter = {};
					criteria.save(filter, tr.children().eq(2), tr.children().eq(3), lang);
					if (criteria.check(filter))
						scope[criteria.name] = filter;
					else
						scope[criteria.name] = filter;
				}
			});
		}
		function status(div) {
			return div.find('tbody > tr').filter(function() { return this.style.display !== 'none'; }).length > 0;
		}
		return new ScopeTab(title, check, clear, load, save, status);
	}

	/** Cette classe représente l'éditeur permettant l'édition d'un univers (= l'intérieur du popin)
	 * 
	 * @member {HTMLELement} target - le div dans lequel créer l'éditeur
	 * @member {string} title - le titre du popin
	 * @member {string} lang - la langue, parmi les langues supportées dans api.langs
	 * @member {array} tabs - la liste des onglets, créés grâce aux méthodes "add*Tab" 
	 * @member {object} scope - l'univers à éditer, chargé progressivement dans l'IHM et modifié si l'univers applique ses modifications
	 * @member {boolean} editable - indique si l'univers peut être modifié (true) ou s'il est affiché pour info, en lecture seule (false)
	 * @member {function} callback - la fonction qui sera appelée si l'utilisateur applique ses modifications dans "save" ou "clear"
	 */
	function ScopeEditor(target, title, lang, tabs, scope, editable, callback) {
		this.target = $(target);
		this.title = title;
		this.lang = lang;
		this.tabs = tabs;
		this.scope = scope;
		this.editable = editable;
		this.callback = callback;
		this.build();
	}

	ScopeEditor.prototype.build = function() {
		var self = this;

		// Création des boutons radio pour choisir l'association en 'ET' ou 'OU'
		var radios = $(''
				+ '<span class="bbm-scope-association" style="float: right; padding: 10px;">'
				+ '  <input type="radio" name="association" id="scopeAssociationAnd" value="and" />'
				+ '  <label for="scopeAssociationAnd">' + this.lang['AndAssociation'] + '</label>'
				+ '  <input type="radio" name="association" id="scopeAssociationOr" value="or" />'
				+ '  <label for="scopeAssociationOr">' + this.lang['OrAssociation'] + '</label>'
				+ '</div>');
		radios.find('input').prop('disabled', !this.editable);
		radios.find('input[value=' + (this.scope.association || 'and') + ']').prop('checked', true);

		// Création des onglets
		var tabs = $('<ul class="nav nav-tabs" style="flex: none; " />');
		tabs.append(this.tabs.map(function(tab) {
			return $('<li role="presentation"><a href="#" /></li>')
				.find('a').text(tab.title).end()
				.toggleClass('bbm-scope-tab-active', tab.check(self.scope))
				.get(0);
		}));

		// Création des conteneurs associés à chaque onglet
		var containers = $('<div style="flex: auto; overflow: hidden; " />');
		containers.append(this.tabs.map(function(tab) {
			return $('<div class="bbm-scope-tab" style="width: 100%; height: 100%; display: none; flex-direction: column; " />').data('loaded', false).get(0);
		}));

		// Gestion du clic sur les onglets
		tabs.on('click', 'a', function(event) {
			// Ajuster l'onglet actif
			var li = $(event.target).closest('li').addClass('active');
			var oldLi = li.siblings('.active').removeClass('active');
			// Charger (si pas encore fait) et afficher le contenu pour l'onglet cliqué
			var index = li.index() - 1/*le div pour et/ou en prepend*/;
			var div = containers.children().hide().eq(index);
			if (!div.data('loaded')) {
				self.tabs[index].load(self.scope, div, self.lang, self.editable);
				div.data('loaded', true);
			}
			div.css('display', 'flex');
			// Ajuster le marqueur de filtre actif sur l'onglet que l'on quitte
			if (oldLi.length > 0) {
				index = oldLi.index() - 1/*le div pour et/ou en prepend*/;
				div = containers.children().eq(index);
				if (div.data('loaded'))
					oldLi.toggleClass('bbm-scope-tab-active', self.tabs[index].status(div));
			}
			event.preventDefault();
		});

		// Ajout dans le DOM
		this.target.css({
			'width': '100%',
			'height': '100%',
			'display': 'flex',
			'flex-direction': 'column',
		});
		this.target.append(tabs.prepend(radios));
		this.target.append(containers);

		// Sélection du 1er onglet
		tabs.find('a').first().click();
	};

	ScopeEditor.prototype.save = function() {
		// Sauvegarder l'association choisie ('ET' / 'OU')
		this.scope.association = this.target.find('.bbm-scope-association :checked').val();
		// Sauvegarder les critères de filtre définis
		this.tabs.forEach(function(tab, index) {
			var div = this.target.find('.bbm-scope-tab').eq(index);
			if (div.data('loaded'))
				tab.save(this.scope, div, this.lang);
		}.bind(this));
		// Appeller la callback avec l'univers mis à jour
		this.callback(this.scope);
	};

	ScopeEditor.prototype.clear = function() {
		// Remettre l'association par défaut ('ET')
		this.scope.association = 'and';
		// Désactiver tous les critères de filtre
		this.tabs.forEach(function(tab) {
			tab.clear(this.scope);
		}.bind(this));
		// Appeller la callback avec l'univers vidé
		this.callback(this.scope);
	};

	/**
	 * Ce plugin crée un popin permettant de définir un univers :
	 *
	 * @member {string} title - le titre du popin
	 * @member {string} langCode - la langue (fr, en, ...), parmi les langues supportées dans api.langs
	 * @member {array} tabs - la liste des onglets, créés grâce aux méthodes "add*Tab" 
	 * @member {object} scope - l'univers à éditer, chargé progressivement dans l'IHM et modifié si l'univers applique ses modifications
	 * @member {boolean} editable - indique si l'univers peut être modifié (true) ou s'il est affiché pour info, en lecture seule (false)
	 * @member {function} callback - la fonction qui sera appelée si l'utilisateur applique ses modifications dans "save" ou "clear"
	 */
	$.fn.materialPopinScope = function(title, langCode, tabs, scope, editable, callback) {
		// Récupération de la langue
		var lang = $.fn.materialPopinScope.langs[langCode];
		// Création du conteneur
		var div = $(this);
		// Création du contenu
		var editor = new ScopeEditor(div, title, lang, tabs, scope, editable, callback);
		// Affichage du popin une fois que tout est prêt
		div.materialPopin({
			title: title,
			closeButtonTitle: lang['CancelButton'],
			buttons : editable ? [{
				icon : 'done',
				visible : true,
				title: lang['ValidateButton'],
				onclick : function validate() {
					editor.save();
					div.materialPopin('close');
				}
			}, {
				icon : 'delete',
				visible : true,
				title: lang['ClearButton'],
				onclick : function clear() {
					editor.clear();
					div.materialPopin('close');
				}
			}] : []
		});
	};

	var api = $.fn.materialPopinScope;

	/** Export des fonctions permettant la création des onglets dans le popin */
	api.createListTab = createListTab;
	api.createListOrTreeTab = createListOrTreeTab;
	api.createGridTab = createGridTab;
	api.createMultiTab = createMultiTab;

	/** Export des fonctions permettant la création de critères simples regroupés en un seul onglet */
	api.createBooleanCriteria = createBooleanCriteria;
	api.createTextCriteria = createTextCriteria;
	api.createNumberCriteria = createNumberCriteria;
	api.createDateTimeCriteria = createDateTimeCriteria;
	api.createListCriteria = createListCriteria;

	/** Traduction des libellés utilisés dans les différentes langues supportées */
	api.langs = {
		fr: {
			Locale: 'fr',
			ValidateButton: 'Valider',
			ClearButton: 'Supprimer tous les filtres',
			CancelButton: 'Annuler',
			AddButton: 'Ajouter un champ',
			AndAssociation: 'et',
			OrAssociation: 'ou',
			FieldColumn: 'Champ',
			OperatorColumn: 'Opérateur / min',
			ValueColumn: 'Valeur / max',
			RemoveButton: 'Supprimer le critère',
			MinPlaceholder: 'Min',
			MaxPlaceholder: 'Max',
			PatternPlaceholder: 'Texte',
			operators: {
				Equal: 'est égal à', Different: 'est différent de',
				IsNull: 'est vide', IsNotNull: 'n\'est pas vide',
				IsTrue: 'est vrai', IsNotTrue: 'n\'est pas vrai',
				IsFalse: 'est faux', IsNotFalse: 'n\'est pas faux',
				Like: 'contient', NotLike: 'ne contient pas'
			}
		},
		en: {
			Locale: 'en',
			ValidateButton: 'Validate',
			ClearButton: 'Clear all filters',
			CancelButton: 'Cancel',
			AddButton: 'Add field',
			AndAssociation: 'and',
			OrAssociation: 'or',
			NumberColumn: 'N°',
			FieldColumn: 'Field',
			OperatorColumn: 'Operator / Min',
			ValueColumn: 'Value / Max',
			RemoveButton: 'Remove field',
			MinPlaceholder: 'Min',
			MaxPlaceholder: 'Max',
			PatternPlaceholder: 'Text',
			operators: {
				Equal: 'equals to', Different: 'is different from',
				IsNull: 'is empty', IsNotNull: 'is not empty',
				IsTrue: 'is true', IsNotTrue: 'is not true',
				IsFalse: 'is false', IsNotFalse: 'is not false',
				Like: 'contains', NotLike: 'does not contain'
			}
		},
		de: {
			Locale: 'de',
			ValidateButton: 'Validieren',
			ClearButton: 'alle Kriterien entfernen',
			CancelButton: 'Abbrechen',
			AddButton: 'ein Kriterium fügen',
			AndAssociation: 'und',
			OrAssociation: 'oder',
			NumberColumn: '',//Anzahl
			FieldColumn: 'Kriterium',
			OperatorColumn: 'Operator',
			ValueColumn: 'Wert',
			RemoveButton: 'das Kriterium löschen',
			MinPlaceholder: 'Min',
			MaxPlaceholder: 'Max',
			PatternPlaceholder: 'Wert',
			operators: {
				Equal: 'ist gleich', Different: 'ist nicht gleich',
				IsNull: 'ist leer', IsNotNull: 'ist nicht leer',
				IsTrue: 'ist wahr', IsNotTrue: 'ist nicht wahr',
				IsFalse: 'ist falsch', IsNotFalse: 'ist nicht falsch',
				Like: 'enthält', NotLike: 'enthält nicht'
			}
		}
	};

})(window.jQuery, window.moment);